import React from 'react';
import { ScheduleDayProps, ScheduleItemProps } from './ScheduleDisplay';
import { ButtonV2, ButtonV2Props, Divider, Flex, FlexProps, TextV2 } from '@withjoy/joykit';
import { formatDateAsDay, formatDateAsTimes } from '../formatDates';
import { DressCode, MapMarker } from '@withjoy/joykit/icons';
import { BedIcon } from './Bed';
import { Directions } from './Directions';
import { useEventHasPage } from '../hooks/useEventHasPage';
import { AddToCalendar as AddToCalendarButton } from '@shared/components/AddToCalendar/AddToCalendar';
import { AddToCalendar } from './AddToCalendar';
import { getCalendarProps, goToAccommodations, goToDirections } from '../navigation';
import { useTranslation } from '@shared/core';

export const ScheduleAction = (props: { icon: React.ReactNode; label: string; onClick?: () => void } & ButtonV2Props) => {
  const { ...restProps } = props;
  return (
    <ButtonV2
      variant="ghost"
      intent="primary"
      height="fit-content"
      padding="0px"
      startIcon={props.icon}
      {...restProps}
    >
      {props.label}
    </ButtonV2>
  );
};

export const IconTextRow = (props: { icon: React.ReactNode; children: React.ReactNode }) => {
  return (
    <Flex gap="16px">
      {props.icon}
      {props.children}
    </Flex>
  );
};

const TwoColumnLayout = (
  props: {
    leftContent: React.ReactNode;
    rightContent: React.ReactNode;
    leftWidth?: string | number;
    rightWidth?: string | number;
  } & FlexProps,
) => {
  const { leftContent, rightContent, leftWidth = 250, rightWidth = 'calc(100% - 250px)', ...containerProps } = props;

  return (
    <Flex
      gap={24}
      width="100%"
      {...containerProps}
    >
      {/* Left Column */}
      <Flex
        width={leftWidth}
        flexDirection="column"
        alignItems="flex-end"
      >
        {leftContent}
      </Flex>
      {/* Right Column */}
      <Flex
        width={rightWidth}
        flexDirection="column"
        alignItems="flex-start"
        gap={24}
      >
        {rightContent}
      </Flex>
    </Flex>
  );
};

const TwoColumnDesktopItem = (props: ScheduleItemProps) => {
  const { data } = props;
  const { startTimeISO8601TZ, endTimeISO8601TZ, name, description, dressCode, location } = data;
  const timeFormatted = formatDateAsTimes(startTimeISO8601TZ, endTimeISO8601TZ);
  const hasAccomPage = useEventHasPage('accommodations');
  const { tq } = useTranslation('generalEventSchedule');

  return (
    <Flex
      flexDirection="column"
      gap={16}
      width="100%"
    >
      {/* Time and Title */}
      <TwoColumnLayout
        leftContent={<TextV2 typographyVariant="label6">{timeFormatted}</TextV2>}
        rightContent={<TextV2 typographyVariant="hed3">{name}</TextV2>}
        leftWidth={250}
        rightWidth="calc(100% - 250px)"
        alignItems="baseline"
      />
      {/* Description and DressCode */}
      <TwoColumnLayout
        leftContent={
          <Flex
            flexDirection="column"
            gap={16}
            alignItems="flex-end"
          >
            {/* conditional on accom page */}
            <AddToCalendarButton {...getCalendarProps(data)}>
              <ButtonV2
                startIcon={<AddToCalendar />}
                variant="ghost"
                intent="primary"
                height="fit-content"
                padding="0px"
              >
                {tq`Add to Calendar`}
              </ButtonV2>
            </AddToCalendarButton>
            {hasAccomPage && (
              <ScheduleAction
                icon={<BedIcon />}
                label={tq`Where to Stay`}
                onClick={goToAccommodations}
              />
            )}
            {location && (
              <ScheduleAction
                icon={<Directions />}
                label={tq`Get Directions`}
                onClick={() => {
                  goToDirections(location);
                }}
              />
            )}
          </Flex>
        } // No left content for this row
        rightContent={
          <>
            {description && <TextV2 typographyVariant="body3">{description}</TextV2>}
            {location && location !== '' && (
              <IconTextRow icon={<MapMarker />}>
                <TextV2 typographyVariant="body3">{location}</TextV2>
              </IconTextRow>
            )}
            {dressCode && dressCode !== '' && (
              <IconTextRow icon={<DressCode />}>
                <TextV2 typographyVariant="body3">{dressCode}</TextV2>
              </IconTextRow>
            )}
          </>
        }
        leftWidth={250}
        rightWidth="calc(100% - 250px)"
      />
    </Flex>
  );
};

export const TwoColumnDesktopDay = (props: ScheduleDayProps) => {
  const { data } = props;
  const date = data.dateISO8601TZ;
  const dateFormatted = formatDateAsDay(date);
  const { items } = props.data;

  return (
    <Flex
      flexDirection="column"
      width="80%"
      alignItems="center"
      justifyContent="center"
      marginBottom={64}
    >
      <TextV2
        typographyVariant="hed3"
        marginBottom={16}
      >
        {dateFormatted}
      </TextV2>
      <Divider marginBottom={40} />
      <Flex
        flexDirection="column"
        gap={40}
        width="100%"
      >
        {items.map((item, index) => (
          <TwoColumnDesktopItem
            key={item.id}
            data={item}
          />
        ))}
      </Flex>
    </Flex>
  );
};
